/**
 * Regras de corte e pontuação do lead. Função pura, rodada no servidor: a IA nunca pontua
 * nem decide quem segue. Os limites vêm da tabela `settings` (editável no admin); os
 * valores abaixo são o padrão quando nada foi configurado.
 */
import type { Imovel, Pagamento, Prazo, Propriedade } from './opcoes';
import type { Regiao } from './regiao';

export interface Regras {
  /** Conta mensal (R$) abaixo da qual a conversa encerra: o sistema não costuma compensar. */
  contaMinima: number;
  /** Fora do estado de SP, só projetos com conta a partir deste valor. */
  contaMinimaForaSp: number;
  /** A partir deste valor (ou indústria e rural), o lead vai marcado para o consultor sênior. */
  contaSenior: number;
}

export const REGRAS_PADRAO: Regras = { contaMinima: 200, contaMinimaForaSp: 5000, contaSenior: 5000 };

export const FAIXAS = ['quente', 'morno', 'frio'] as const;
export type Faixa = (typeof FAIXAS)[number];
export const FAIXA_LABEL: Record<Faixa, string> = { quente: 'Quente', morno: 'Morno', frio: 'Frio' };

/** Pontos mínimos de cada faixa. Abaixo de `morno`, frio. */
export const LIMITES_FAIXA = { quente: 70, morno: 40 } as const;

export interface CasoPontuavel {
  imovel?: Imovel;
  conta?: number;
  regiao?: Regiao;
  propriedade?: Propriedade;
  pagamento?: Pagamento;
  prazo?: Prazo;
}

export interface Pontuacao {
  pontos: number;
  faixa: Faixa;
  /** Vai direto para o consultor sênior. */
  senior: boolean;
  criterios: { criterio: string; pontos: number }[];
}

function pontosConta(conta: number | undefined): number {
  if (conta === undefined) return 0;
  if (conta >= 2000) return 35;
  if (conta >= 800) return 25;
  if (conta >= 300) return 15;
  return 0;
}

const PONTOS_REGIAO: Record<Regiao, number> = {
  capital: 20,
  grande_sp: 20,
  litoral_sp: 10,
  interior_sp: 10,
  // Só chega aqui com conta acima do mínimo fora de SP: projeto grande.
  fora_sp: 15,
  desconhecida: 5,
};

const PONTOS_PROPRIEDADE: Record<Propriedade, number> = { proprio: 15, construindo: 10, alugado: 5 };
const PONTOS_PRAZO: Record<Prazo, number> = { '30_dias': 20, '1_3_meses': 10, pesquisando: 0 };
const PONTOS_PAGAMENTO: Record<Pagamento, number> = { a_vista: 10, financiado: 5, nao_sei: 0 };

/** Pontua de 0 a 100: conta (35), região (20), imóvel próprio (15), prazo (20) e pagamento (10). */
export function pontuar(caso: CasoPontuavel, regras: Regras): Pontuacao {
  const criterios = [
    { criterio: 'conta', pontos: pontosConta(caso.conta) },
    { criterio: 'regiao', pontos: caso.regiao ? PONTOS_REGIAO[caso.regiao] : 0 },
    { criterio: 'propriedade', pontos: caso.propriedade ? PONTOS_PROPRIEDADE[caso.propriedade] : 0 },
    { criterio: 'prazo', pontos: caso.prazo ? PONTOS_PRAZO[caso.prazo] : 0 },
    { criterio: 'pagamento', pontos: caso.pagamento ? PONTOS_PAGAMENTO[caso.pagamento] : 0 },
  ];
  const pontos = criterios.reduce((s, c) => s + c.pontos, 0);
  const faixa: Faixa = pontos >= LIMITES_FAIXA.quente ? 'quente' : pontos >= LIMITES_FAIXA.morno ? 'morno' : 'frio';
  const senior = (caso.conta ?? 0) >= regras.contaSenior || caso.imovel === 'industria' || caso.imovel === 'rural';
  return { pontos, faixa, senior, criterios };
}
